// infrastructure/analytics/revenueCycleAnalytics.js
import { getIsPosthogEnabled, posthog } from 'boot/posthog';

/**
 * Product analytics for the Billing screens: how a pilot clinic moves from setting up to exporting.
 *
 * NOT A `CoreAnalytics` SUBCLASS, because that class reads `store/auth`, whose actions import
 * `services/analytics`, which extends `CoreAnalytics` again. Reached from inside a context, the
 * class is still undefined when that chain comes back to it. The user, role and clinic still arrive
 * on every event: `identifyUser` registers them for the session. `clinicId` is passed explicitly as
 * well, because a user linked through `usersClients` edits a client in a clinic other than their own.
 *
 * NOTHING ABOUT A CLIENT LEAVES THROUGH HERE. No client id, member id, diagnosis or date of service:
 * a funding source id is clinic configuration, and everything else is a count. The export's timing
 * travels as day counts computed in the browser for the same reason, never as the dates themselves.
 */

export const EVENT_NAMES = {
  COVERAGE_ADDED: 'coverage_added',
  AUTHORIZATION_ADDED: 'authorization_added',
  BILLING_ITEMS_EXPORTED: 'billing_items_exported',
};

function track(eventName, props) {
  if (!getIsPosthogEnabled()) {
    return;
  }

  try {
    posthog.capture(eventName, { ...props, eventVersion: 1 });
  } catch (error) {
    // Analytics must never break the save or the export it follows.
    if (process.env.ENV_TYPE !== 'production') {
      console.error(error);
    }
  }
} // track()

/**
 * One per coverage created on a client, once the save has landed.
 *
 * @param {{ clinicId: string, fundingSourceId: string|null }} payload
 */
export function trackCoverageAdded({ clinicId, fundingSourceId }) {
  track(EVENT_NAMES.COVERAGE_ADDED, { clinicId, fundingSourceId });
} // trackCoverageAdded()

/**
 * One per authorization recorded on a client, once the save has landed.
 *
 * @param {{ clinicId: string, fundingSourceId: string|null }} payload
 */
export function trackAuthorizationAdded({ clinicId, fundingSourceId }) {
  track(EVENT_NAMES.AUTHORIZATION_ADDED, { clinicId, fundingSourceId });
} // trackAuthorizationAdded()

/**
 * One per export file whose billable rows were stamped as exported.
 *
 * @param {import('../../ui/presenters/billingExportPresenter').ExportUsage
 *   & { clinicId: string, stamped: number, skipped: number }} payload
 */
export function trackBillingItemsExported(payload) {
  track(EVENT_NAMES.BILLING_ITEMS_EXPORTED, payload);
} // trackBillingItemsExported()
